/*
 * Configurateur de coffret découverte.
 * État : Map(sampleId -> quantité). L'ajout au panier envoie le produit coffret avec les
 * échantillons en line item properties numérotées (mécanisme du site actuel).
 * Écoute l'événement `coffret:filters` : detail {available, values: {clé: valeurs cochées}}.
 */
(() => {
  class AxCoffret extends HTMLElement {
    connectedCallback() {
      this.max = parseInt(this.dataset.max, 10) || 0;
      // 0 = pas de plafond par référence (case décochée dans le customizer).
      this.maxPerSample = parseInt(this.dataset.maxPerSample, 10) || 0;
      this.selection = new Map();
      // Journal d'insertion : la boîte et les line item properties suivent l'ordre d'ajout
      // (comme le site actuel), alors que le récapitulatif regroupe par échantillon avec une
      // quantité — c'est ce que demande la maquette.
      this.order = [];
      this.samples = new Map();

      this.querySelectorAll('[data-sample-meta]').forEach((item) => {
        try {
          const meta = JSON.parse(item.getAttribute('data-sample-meta'));
          this.samples.set(String(meta.id), { ...meta, element: item });
        } catch (e) {
          /* item ignoré si meta illisible */
        }
      });

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('coffret:filters', this.onFilters.bind(this));


      // En mobile le dépliage est porté par le volet, pas par le `details`. On l'ouvre une
      // fois pour toutes ET on sort le panneau de sélection du `details` : Chrome enveloppe
      // le contenu d'un `details` dans `::details-content`, qui devient l'élément de grille
      // et se place en colonne 1. Le neutraliser en CSS ne marcherait que sur Chrome récent,
      // alors que déplacer le nœud règle le cas dans tous les navigateurs.
      const details = this.querySelector('.ax-coffret__selection-details');
      const panneau = this.querySelector('.ax-coffret__panel');
      const selection = this.querySelector('[data-coffret-selection]');
      if (details && panneau && selection && window.matchMedia('(max-width: 699px)').matches) {
        details.open = true;
        panneau.append(selection);
      }

      // Suggestion : tout masquer avant le premier appel laisse `renderSuggestion` choisir,
      // plutôt que de garder la carte rendue par Liquid.
      this.suggestions = [...this.querySelectorAll('[data-coffret-suggestion-card]')].map((element) => ({
        element,
        id: String(element.querySelector('[data-coffret-add]')?.dataset.coffretAdd),
      }));
      this.suggestions.forEach(({ element }) => element.toggleAttribute('hidden', true));
      this.renderSuggestion();
    }

    get count() {
      return this.order.length;
    }

    onClick(event) {
      const add = event.target.closest('[data-coffret-add]');
      if (add) return this.change(add.dataset.coffretAdd, 1);

      const remove = event.target.closest('[data-coffret-remove]');
      if (remove) return this.change(remove.dataset.coffretRemove, -1);
    }

    change(id, delta) {
      id = String(id);
      const current = this.selection.get(id) || 0;

      if (delta > 0) {
        if (this.count >= this.max) return;
        if (this.maxPerSample && current >= this.maxPerSample) return;
        this.order.push(id);
      } else {
        if (current === 0) return;
        // On retire la DERNIÈRE occurrence : le - d'une ligne défait le dernier ajout de cet
        // échantillon, sans déplacer les autres dans la boîte.
        this.order.splice(this.order.lastIndexOf(id), 1);
      }

      const next = Math.max(0, current + delta);
      if (next === 0) this.selection.delete(id);
      else this.selection.set(id, next);

      this.render();
    }

    render() {
      const count = this.count;
      const full = count >= this.max;

      // compteur + jauge
      this.querySelectorAll('[data-coffret-count]').forEach((counter) => {
        counter.textContent = `${count}/${this.max}`;
      });

      const fill = this.max > 0 ? (count / this.max) * 100 : 0;
      this.querySelectorAll('[data-coffret-gauge]').forEach((gauge) => {
        gauge.style.setProperty('--ax-coffret-gauge-fill', `${fill}%`);
      });

      // steppers
      this.querySelectorAll('[data-coffret-qty]').forEach((el) => {
        const qty = this.selection.get(el.dataset.coffretQty) || 0;
        el.textContent = qty;
        const item = el.closest('[data-sample]');
        if (item) item.classList.toggle('is-selected', qty > 0);
        const minus = item && item.querySelector('[data-coffret-remove]');
        if (minus) minus.disabled = qty === 0;
      });
      this.querySelectorAll('.ax-coffret__list [data-coffret-add]').forEach((btn) => {
        const qty = this.selection.get(String(btn.dataset.coffretAdd)) || 0;
        btn.disabled = full || (this.maxPerSample > 0 && qty >= this.maxPerSample);
      });

      // aperçu dans la boîte
      const preview = this.querySelector('[data-coffret-preview]');
      if (preview) {
        const thumbs = this.order.map((id) => {
          const sample = this.samples.get(id);
          const img = document.createElement('img');
          if (sample && sample.image) img.src = sample.image;
          img.alt = '';
          img.className = 'ax-coffret__box-thumb';
          return img;
        });
        preview.replaceChildren(...thumbs);
      }

      // lignes du détail de sélection
      const panel = this.querySelector('[data-coffret-selection]');
      if (panel) {
        panel.replaceChildren(...this.selectedEntries().map(([id, qty]) => {
          const sample = this.samples.get(id);
          const row = document.createElement('div');
          row.className = 'ax-coffret__selection-row';
          row.innerHTML = `<span class="ax-coffret__selection-name"></span>
            <span class="ax-coffret__selection-controls">
              <button type="button" class="ax-coffret__step" data-coffret-remove="${id}"><svg aria-hidden="true" width="12" viewBox="0 0 12 12" fill="none"><path stroke="currentColor" d="M0 6h12"/></svg></button>
              <span class="ax-coffret__item-qty">${qty}</span>
              <button type="button" class="ax-coffret__step" data-coffret-add="${id}"><svg aria-hidden="true" width="12" viewBox="0 0 12 12" fill="none"><path stroke="currentColor" d="M6 0v12M0 6h12"/></svg></button>
            </span>`;
          row.querySelector('.ax-coffret__selection-name').textContent = sample ? sample.title : id;
          return row;
        }));
      }

      // bouton d'achat : actif uniquement coffret complet. `max` à 0 signifie que le
      // nombre d'emplacements n'a pas pu être lu : sans ce garde, `0 >= 0` rendrait le
      // bouton actif et enverrait le coffret au panier sans aucun échantillon.
      const incomplete = this.max < 1 || count < this.max;
      this.querySelectorAll('[data-coffret-submit]').forEach((submit) => {
        submit.disabled = incomplete;
      });

      this.renderSuggestion();
      this.syncProperties();
    }

    /*
     * Un produit suggéré hors de la liste d'échantillons n'a ni vignette ni titre : son `+`
     * enverrait un identifiant numérique en line item property. On ne le propose donc jamais.
     */
    renderSuggestion() {
      const block = this.querySelector('[data-coffret-suggestion]');
      if (!block) return;

      const eligible = this.suggestions.filter(({ id }) => this.samples.has(id) && !this.selection.has(id));
      block.toggleAttribute('hidden', eligible.length === 0);
      if (eligible.length === 0) return;

      // Le `+` suit les boutons du catalogue : grisé dès que le coffret est plein.
      const full = this.max > 0 && this.count >= this.max;
      this.suggestions.forEach(({ element }) => {
        const add = element.querySelector('[data-coffret-add]');
        if (add) add.disabled = full;
      });

      // La carte ne change que lorsqu'elle entre dans la boîte : la relancer à chaque ajout la
      // ferait clignoter sur des clics qui ne la concernent pas. La suivante est la première
      // de la liste du customizer encore disponible, l'ordre des produits portant la priorité
      // de mise en avant.
      const shown = this.suggestions.find(({ element }) => !element.hidden);
      if (shown && eligible.includes(shown)) return;

      const pick = eligible[0];

      this.suggestions.forEach(({ element }) => element.toggleAttribute('hidden', element !== pick.element));
    }

    selectedEntries() {
      return [...this.selection.entries()];
    }

    /*
     * Les line item properties sont reconstruites à CHAQUE changement de sélection, et non
     * au `submit` : le `product-form` du thème sérialise le formulaire dans son propre
     * écouteur de `submit`, et l'ordre d'exécution des deux écouteurs dépend de l'ordre
     * d'upgrade des éléments personnalisés. Mesuré : les champs étaient bien créés, mais
     * après la sérialisation, et la ligne partait au panier avec `properties: {}`.
     */
    syncProperties() {
      const holder = this.querySelector('[data-coffret-properties]');
      if (!holder) return;
      holder.replaceChildren();

      // Chaque emplacement porte « Titre — SKU », format repris tel quel du site d'avant la
      // refonte : la chaîne logistique découpe sur ce séparateur pour préparer le coffret.
      // Le client, lui, ne voit que le titre, `line-item.liquid` coupe à l'affichage.
      this.order.forEach((id, i) => {
        const sample = this.samples.get(id);
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${i + 1}]`;
        input.value = sample ? [sample.title, sample.sku].filter(Boolean).join(' — ') : id;
        holder.append(input);
      });
    }

    onFilters(event) {
      const { available = false, values = {} } = event.detail || {};

      this.samples.forEach((sample) => {
        let show = !(available && !sample.available);

        // OU à l'intérieur d'un groupe, ET entre les groupes — le comportement des filtres
        // natifs Shopify. Les groupes viennent du panneau, ce script n'en connaît aucun.
        if (show) {
          show = Object.entries(values).every(
            ([key, selected]) => !selected.length || selected.some((value) => (sample[key] || []).includes(value))
          );
        }

        sample.element.toggleAttribute('hidden', !show);
      });
    }
  }

  if (!customElements.get('ax-coffret')) customElements.define('ax-coffret', AxCoffret);
})();
